import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, ExternalLink, Save, Check, X as XIcon } from "lucide-react";
import { AdminModal, AdminField, ImageUploadField } from "@/pages/admin/AdminBits";

const emptyPopup = () => ({
  title: "",
  image_url: "",
  link_url: "",
  active: true,
  display_delay_seconds: 5,
  auto_close_seconds: 5,
  starts_at: "",
  ends_at: "",
});

export default function AdminPopups() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState(emptyPopup());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/popups");
      setItems(data);
    } catch (e) {
      toast.error("Popup listesi yüklenemedi");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setF(emptyPopup()); setOpen(true); };
  const startEdit = (p) => { setEditing(p); setF({ ...emptyPopup(), ...p }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.image_url) { toast.error("Görsel yüklemelisiniz"); return; }
    try {
      const payload = {
        ...f,
        display_delay_seconds: Number(f.display_delay_seconds) || 0,
        auto_close_seconds: Number(f.auto_close_seconds) || 0,
      };
      if (editing) await api.put(`/popups/${editing.id}`, payload);
      else await api.post("/popups", payload);
      toast.success(editing ? "Güncellendi" : "Oluşturuldu");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu pop-up silinsin mi?")) return;
    try { await api.delete(`/popups/${id}`); toast.success("Silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/popups/${p.id}`, { ...p, active: !p.active });
      load();
    } catch { toast.error("Güncellenemedi"); }
  };

  const onChange = (e) => setF({ ...f, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  return (
    <div data-testid="admin-popups">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#102A43]">Pop-up Yönetimi</h1>
          <p className="text-sm text-[#4B5563] mt-1">Ana sayfada gösterilen açılır duyuru görsellerini yönetin.</p>
        </div>
        <button onClick={startCreate} className="btn-primary" data-testid="popup-create-btn">
          <Plus className="w-4 h-4"/> Yeni Pop-up
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#4B5563]">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#E5E7EB] rounded-2xl">
          <p className="text-[#4B5563]">Henüz pop-up eklenmemiş.</p>
          <button onClick={startCreate} className="btn-primary mt-4 inline-flex"><Plus className="w-4 h-4"/> İlk Pop-up&apos;ı Ekle</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(p => (
            <div key={p.id} className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden" data-testid={`popup-card-${p.id}`}>
              <div className="aspect-video bg-[#F7F9FA] overflow-hidden">
                {p.image_url ? <img src={fileUrl(p.image_url)} alt="" className="w-full h-full object-contain"/> : <div className="w-full h-full flex items-center justify-center text-[#4B5563] text-xs">Görsel yok</div>}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-heading font-bold text-[#102A43] text-sm truncate">{p.title || "(Başlıksız)"}</h3>
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${p.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {p.active ? "Aktif" : "Pasif"}
                  </span>
                </div>
                <p className="text-xs text-[#4B5563] mt-1">Gecikme: {p.display_delay_seconds}s · Kapanış: {p.auto_close_seconds}s</p>
                {p.link_url && <a href={p.link_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#1F8A5B] hover:underline"><ExternalLink className="w-3 h-3"/> Bağlantı</a>}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => toggleActive(p)} className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-[#E5E7EB] hover:border-[#1F8A5B]" data-testid={`popup-toggle-${p.id}`}>
                    {p.active ? <><XIcon className="w-3 h-3 inline mr-1"/>Devre Dışı</> : <><Check className="w-3 h-3 inline mr-1"/>Aktifleştir</>}
                  </button>
                  <button onClick={() => startEdit(p)} className="p-1.5 rounded-lg border border-[#E5E7EB] hover:border-[#1F8A5B]" data-testid={`popup-edit-${p.id}`}><Edit3 className="w-3.5 h-3.5"/></button>
                  <button onClick={() => remove(p.id)} className="p-1.5 rounded-lg border border-[#E5E7EB] hover:border-red-400 hover:text-red-500" data-testid={`popup-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminModal open={open} onClose={() => setOpen(false)} title={editing ? "Pop-up Düzenle" : "Yeni Pop-up"} testid="popup-modal">
        <form onSubmit={submit} className="space-y-4" data-testid="popup-form">
          <AdminField label="Başlık (Opsiyonel)" name="title" value={f.title} onChange={onChange} testid="popup-input-title"/>
          <ImageUploadField label="Pop-up Görseli *" value={f.image_url} onChange={(url) => setF({ ...f, image_url: url })} testid="popup-input-image"/>
          <AdminField label="Bağlantı URL (opsiyonel)" name="link_url" value={f.link_url} onChange={onChange} testid="popup-input-link"/>
          <div className="grid grid-cols-2 gap-3">
            <AdminField label="Gösterim Gecikmesi (sn)" name="display_delay_seconds" type="number" value={f.display_delay_seconds} onChange={onChange} testid="popup-input-delay"/>
            <AdminField label="Otomatik Kapanma (sn) — 0 = kapanmaz" name="auto_close_seconds" type="number" value={f.auto_close_seconds} onChange={onChange} testid="popup-input-autoclose"/>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#102A43]">
            <input type="checkbox" name="active" checked={!!f.active} onChange={onChange} data-testid="popup-input-active"/>
            Aktif (Ana sayfada göster)
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t border-[#E5E7EB]">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline" data-testid="popup-cancel">İptal</button>
            <button type="submit" className="btn-primary" data-testid="popup-save"><Save className="w-4 h-4"/> Kaydet</button>
          </div>
        </form>
      </AdminModal>
    </div>
  );
}
